import {
    Add as AddIcon,
    Book,
    Edit as EditIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';



// Status chip color mapping
const statusColors = {
    active: 'success',
    pending: 'warning',
    archive: 'error',
};

const CoursesPage = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current_page: 1,
        limit: 10,
        total_records: 0,
        total_pages: 1
    });
    const apiURL = process.env.REACT_APP_API;
    const location = useLocation();
    const navigate = useNavigate();

    // Get page from URL query parameter
    const getPageFromURL = () => {
        const params = new URLSearchParams(location.search);
        const page = parseInt(params.get('page')) || 1;
        return page;
    };

    // Fetch courses on component mount and when URL changes
    useEffect(() => {
        const currentPage = getPageFromURL();
        fetchCourses(currentPage);
    }, [location.search]);

    // Fetch courses from API
    const fetchCourses = async (page = 1) => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiURL}/admin/course/getall`, {
                params: { page }
            });

            if (response.data.status === 'success' || response.data.status === true) {
                setCourses(response.data.data || []);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            } else {
                toast.error(response.data.message || 'Failed to fetch courses');
            }
        } catch (error) {
            console.error('Error fetching courses:', error);
            toast.error(error.response?.data?.message || 'An error occurred while fetching courses');
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (event, value) => {
        navigate(`?page=${value}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };



    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <Box className='content' pb={5} >
            <Container>
                {/* Header with Add Button */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', }} gap={2} alignItems={{ md: 'center', xs: 'start' }} flexDirection={{ md: 'row', xs: 'column' }} >
                    <Box>
                        <Typography variant="h4" fontWeight={'bold'} >
                            Courses Management
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Manage your course offerings, pricing, and materials
                        </Typography>
                    </Box>
                    <NavLink to={'/controller/courses/add'}>
                        <Button variant="contained" startIcon={<AddIcon />}  >
                            Add New Course
                        </Button>
                    </NavLink>
                </Box>

                {/* Courses Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }} color="text.secondary">Loading courses...</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} elevation={1}>
                            <Table size='small' >
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell><strong>Title</strong></TableCell>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Description</strong></TableCell>
                                        <TableCell><strong>Duration</strong></TableCell>
                                        <TableCell><strong>Pricing</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {courses.map((course) => (
                                        <TableRow
                                            key={course.id}
                                            hover
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {course.c_title}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {course.c_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 300 }}>
                                                <Typography variant="body2" noWrap>
                                                    {course.c_description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={`${course.c_duration} months`} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                        {formatCurrency(course.c_max_price)}
                                                    </Typography>
                                                    <Typography variant="body1" color="primary.main" fontWeight="bold">
                                                        {formatCurrency(course.c_price)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={course.c_status.charAt(0).toUpperCase() + course.c_status.slice(1)}
                                                    color={statusColors[course.c_status]}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction={'row'} spacing={1} >
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        component={NavLink}
                                                        to={`/controller/course-subject?courseid=${course.courseid}`}
                                                        startIcon={<Book />}
                                                    >
                                                        Subjects
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        component={NavLink}
                                                        to={`/controller/courses/update?courseid=${course.courseid}`}
                                                        startIcon={<EditIcon />}
                                                    >
                                                        Edit
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Empty State */}
                        {courses.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No courses found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Click "Add New Course" to create your first course
                                </Typography>
                            </Box>
                        )}

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Showing {((pagination.current_page - 1) * pagination.limit) + 1} to {Math.min(pagination.current_page * pagination.limit, pagination.total_records)} of {pagination.total_records} courses
                                </Typography>
                                <Pagination
                                    count={pagination.total_pages}
                                    page={pagination.current_page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
};

export default CoursesPage;